import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch, useApp } from '../bizsuite-app';
import { formatCurrencyShort } from '../utils/currency';

function Skeleton({ width, height, style }) {
  return (
    <div style={{
      width: width || '100%', height: height || 20,
      background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', borderRadius: 6, ...style,
    }} />
  );
}

function HBarChart({ data, labelKey, valueKey, maxValue, formatValue, colorFn }) {
  if (!data || data.length === 0) return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No data</div>;
  const max = maxValue || Math.max(...data.map(d => parseFloat(d[valueKey]) || 0), 1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map((item, idx) => {
        const val = parseFloat(item[valueKey]) || 0;
        const w = (val / max) * 100;
        const color = colorFn ? colorFn(item, idx) : '#6366f1';
        return (
          <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 120, fontSize: 12, color: '#64748B', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item[labelKey]}>
              {item[labelKey] || 'Unknown'}
            </div>
            <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 22, overflow: 'hidden' }}>
              <div style={{ width: `${Math.max(w, 2)}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.6s ease' }} />
            </div>
            <div style={{ width: 70, fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right', flexShrink: 0 }}>
              {formatValue ? formatValue(val) : val.toLocaleString()}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, color, loading }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: 20, border: '1px solid #e2e8f0',
      display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 12, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      {loading ? <Skeleton width={80} height={32} /> : (
        <div style={{ fontSize: 28, fontWeight: 700, color: color || '#1e293b' }}>{value}</div>
      )}
      {sub && <div style={{ fontSize: 12, color: '#94A3B8' }}>{sub}</div>}
    </div>
  );
}

export default function SalesDashboard() {
  const app = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/dashboards/sales');
      if (res.ok) setData(await res.json());
    } catch (e) { /* best effort */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const d = data || {};
  const stageColors = { Prospect: '#6366f1', Qualified: '#8b5cf6', Proposal: '#a855f7', Negotiation: '#f59e0b', Won: '#10b981', Lost: '#ef4444' };

  return (
    <div>
      <style>{`@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }`}</style>

      <p style={{ color: '#64748B', marginBottom: 20, marginTop: -16 }}>
        Sales performance and pipeline overview.
      </p>

      {/* Stat Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <StatCard label="Revenue Forecast" value={formatCurrencyShort(d.revenue_forecast)} color="#10b981" loading={loading} />
        <StatCard label="Lead Conversion" value={`${d.lead_conversion_rate || 0}%`} sub={`${d.converted_leads || 0} of ${d.total_leads || 0}`} color="#6366f1" loading={loading} />
        <StatCard label="Proposals Won" value={d.proposals_summary?.won || 0} sub={`${d.proposals_summary?.total || 0} this month`} color="#10b981" loading={loading} />
        <StatCard label="Proposals Pending" value={d.proposals_summary?.pending || 0} sub={`${d.proposals_summary?.submitted || 0} submitted`} color="#f59e0b" loading={loading} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Pipeline Funnel */}
        <div className="card">
          <div className="card-header"><h2>Pipeline Funnel</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3,4,5].map(i => <Skeleton key={i} height={28} />)}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {(d.pipeline_by_stage || []).map((stage, idx) => {
                  const maxVal = Math.max(...(d.pipeline_by_stage || []).map(s => parseFloat(s.total_value) || 0), 1);
                  const val = parseFloat(stage.total_value) || 0;
                  const w = (val / maxVal) * 100;
                  const color = stageColors[stage.stage] || '#94A3B8';
                  return (
                    <div key={idx}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                        <span style={{ color: '#334155', fontWeight: 600 }}>{stage.stage}</span>
                        <span style={{ color: '#64748B' }}>{stage.deal_count} deals &middot; {formatCurrencyShort(val)}</span>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 6, height: 28, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.max(w, 3)}%`, background: color, height: '100%', borderRadius: 6, display: 'flex', alignItems: 'center', paddingLeft: 8, color: '#fff', fontSize: 11, fontWeight: 600, transition: 'width 0.6s ease' }}>
                          {w > 15 ? formatCurrencyShort(val) : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Customer Health */}
        <div className="card">
          <div className="card-header"><h2>Customer Health</h2></div>
          <div className="card-body">
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
            ) : (
              <HBarChart
                data={d.customer_health_distribution || []}
                labelKey="band"
                valueKey="count"
                colorFn={(item) => item.band === 'Healthy' ? '#10b981' : item.band === 'At Risk' ? '#f59e0b' : '#ef4444'}
              />
            )}
          </div>
        </div>
      </div>

      {/* Activity by Rep */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>Activity by Sales Rep (This Month)</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (d.activity_by_rep || []).length === 0 ? (
            <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No activity data</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B', fontWeight: 600 }}>Rep</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B', fontWeight: 600 }}>Calls</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B', fontWeight: 600 }}>Meetings</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B', fontWeight: 600 }}>Emails</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B', fontWeight: 600 }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.activity_by_rep || []).map((rep, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{rep.rep_name}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>{rep.calls}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>{rep.meetings}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>{rep.emails}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px', fontWeight: 600 }}>{rep.total_activities}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Top Opportunities */}
      <div className="card">
        <div className="card-header"><h2>Top Opportunities</h2></div>
        <div className="card-body">
          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{[1,2,3].map(i => <Skeleton key={i} height={22} />)}</div>
          ) : (d.top_opportunities || []).length === 0 ? (
            <div style={{ color: '#94A3B8', textAlign: 'center', padding: 16 }}>No open opportunities</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Opportunity</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', color: '#64748B' }}>Customer</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B' }}>Stage</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#64748B' }}>Value</th>
                    <th style={{ textAlign: 'center', padding: '8px 12px', color: '#64748B' }}>Prob</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', color: '#64748B' }}>Weighted</th>
                  </tr>
                </thead>
                <tbody>
                  {(d.top_opportunities || []).map((opp, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{opp.name}</td>
                      <td style={{ padding: '8px 12px', color: '#64748B' }}>{opp.customer_name || '-'}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: stageColors[opp.stage] || '#e2e8f0', color: '#fff' }}>
                          {opp.stage}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', padding: '8px 12px' }}>{formatCurrencyShort(opp.estimated_value)}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>{opp.probability_pct}%</td>
                      <td style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 600 }}>{formatCurrencyShort(opp.weighted_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
